import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * PLEPerformanceTrendsChart (Bare) - PLE performance over time
 * Shows grade distribution and pass rate trends
 * For embedding in narrative HTML templates
 */
function PLEPerformanceTrendsChart({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate by year and grade
    const aggregatedData = {}

    data.forEach(record => {
      const year = record.year
      const grade = record.grade
      const value = parseInt(record.value) || 0

      if (!aggregatedData[year]) {
        aggregatedData[year] = {
          'Division 1': 0,
          'Division 2': 0,
          'Division 3': 0,
          'Division 4': 0,
          U: 0,
          X: 0,
        }
      }

      if (aggregatedData[year][grade] !== undefined) {
        aggregatedData[year][grade] += value
      }
    })

    // Convert to array format
    const years = Object.keys(aggregatedData).sort()
    const div1Data = years.map(year => aggregatedData[year]['Division 1'] || 0)
    const div2Data = years.map(year => aggregatedData[year]['Division 2'] || 0)
    const div3Data = years.map(year => aggregatedData[year]['Division 3'] || 0)
    const div4Data = years.map(year => aggregatedData[year]['Division 4'] || 0)
    const ungradedData = years.map(year => aggregatedData[year]['U'] || 0)

    // Calculate pass rate (Division 1-4)
    const passRateData = years.map(year => {
      const total =
        aggregatedData[year]['Division 1'] +
        aggregatedData[year]['Division 2'] +
        aggregatedData[year]['Division 3'] +
        aggregatedData[year]['Division 4'] +
        aggregatedData[year]['U'] +
        aggregatedData[year]['X']
      const passed =
        aggregatedData[year]['Division 1'] +
        aggregatedData[year]['Division 2'] +
        aggregatedData[year]['Division 3'] +
        aggregatedData[year]['Division 4']
      return total > 0 ? ((passed / total) * 100).toFixed(1) : 0
    })

    return {
      years,
      div1Data,
      div2Data,
      div3Data,
      div4Data,
      ungradedData,
      passRateData,
    }
  }, [data])

  const option = useMemo(() => {
    if (!chartData) return null

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: function (params) {
          let content = `<strong>${params[0].axisValue}</strong><br/>`
          params.forEach(param => {
            if (param.seriesName === 'Pass Rate') {
              content += `${param.seriesName}: ${param.value}%<br/>`
            } else {
              content += `${param.seriesName}: ${param.value}<br/>`
            }
          })
          return content
        },
      },
      legend: {
        bottom: 10,
        itemGap: 15,
        textStyle: { fontSize: 11 },
      },
      grid: {
        left: 60,
        right: 60,
        top: 30,
        bottom: 70,
      },
      xAxis: {
        type: 'category',
        data: chartData.years,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#6b7280' },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Count',
          position: 'left',
          axisLine: { lineStyle: { color: '#e5e7eb' } },
          axisLabel: { color: '#6b7280' },
          splitLine: { lineStyle: { color: '#f3f4f6' } },
        },
        {
          type: 'value',
          name: 'Pass Rate %',
          position: 'right',
          min: 0,
          max: 100,
          axisLine: { lineStyle: { color: '#e5e7eb' } },
          axisLabel: {
            color: '#6b7280',
            formatter: '{value}%',
          },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: 'Division 1',
          data: chartData.div1Data,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#10B981' },
        },
        {
          name: 'Division 2',
          data: chartData.div2Data,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#3B82F6' },
        },
        {
          name: 'Division 3',
          data: chartData.div3Data,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#F59E0B' },
        },
        {
          name: 'Division 4',
          data: chartData.div4Data,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#EF4444' },
        },
        {
          name: 'Ungraded',
          data: chartData.ungradedData,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#9CA3AF' },
        },
        {
          name: 'Pass Rate',
          data: chartData.passRateData,
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          itemStyle: { color: '#8B5CF6' },
          lineStyle: { width: 3 },
          label: {
            show: true,
            formatter: '{c}%',
            fontSize: 10,
          },
        },
      ],
    }
  }, [chartData])

  if (!data || !chartData) {
    return (
      <div className="text-center py-8 text-gray-500">
        No PLE performance data available
      </div>
    )
  }

  return <ReactECharts option={option} style={{ height: '450px' }} />
}

PLEPerformanceTrendsChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      grade: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
}

export default PLEPerformanceTrendsChart
